/**
 * WordPress dependencies
 */
import { isDefined } from '@nab/utils';
import {
	Button,
	CheckboxControl,
	SelectControl,
	TextControl,
} from '@safe-wordpress/components';
import { useState } from '@safe-wordpress/element';
import { _x } from '@safe-wordpress/i18n';
import clsx from 'clsx';

/**
 * WordPress dependencies
 */
import { isEqual, trim, uniq, without } from 'lodash';

export type ManageContentProps<
	T extends { readonly attributes: { readonly name: string } },
> = {
	readonly items: ReadonlyArray< T >;
	readonly labels: {
		readonly add?: string;
		readonly manageElements?: string;
		readonly replace?: string;
		readonly selectMultiple?: string;
		readonly selectSingle?: string;
	};
	readonly isSingular?: boolean;
	readonly saving: boolean;
	readonly onClose: () => void;
	readonly onAdd?: ( items: ReadonlyArray< NoInfer< T > > ) => void;
	readonly onReplace?: ( items: ReadonlyArray< NoInfer< T > > ) => void;
	readonly onSave?: (
		items: ReadonlyArray< NoInfer< T > >
	) => Promise< unknown >;
};

export function ManageContent<
	T extends { readonly attributes: { readonly name: string } },
>( {
	items,
	labels,
	saving,
	isSingular,
	onClose,
	onAdd,
	onReplace,
	onSave,
}: ManageContentProps< T > ): JSX.Element {
	const [ isEditing, markAsEditing ] = useState( false );
	const [ selection, setSelection ] = useState< ReadonlyArray< string > >(
		isSingular && items[ 0 ] ? [ items[ 0 ].attributes.name ] : []
	);

	if ( onSave && isEditing ) {
		return (
			<Editor
				items={ items }
				labels={ labels }
				saving={ saving }
				onSave={ async ( newItems ) => {
					await onSave( newItems );
					if ( newItems.length ) {
						markAsEditing( false );
					} else {
						onClose();
					}
				} }
				onCancel={ () => markAsEditing( false ) }
			/>
		);
	}

	return (
		<>
			<div className="nelio-ab-testing-reusable-entities-dialog__content">
				{ isSingular ? (
					<>
						{ !! labels.selectSingle && (
							<div>{ labels.selectSingle }</div>
						) }
						<SelectControl
							options={ items.map( ( i ) => ( {
								value: i.attributes.name,
								label: i.attributes.name,
							} ) ) }
							value={ selection[ 0 ] }
							onChange={ ( v ) => setSelection( [ v ] ) }
						/>
					</>
				) : (
					<>
						{ !! labels.selectMultiple && (
							<div>{ labels.selectMultiple }</div>
						) }
						{ items.map( ( { attributes: { name } } ) => (
							<CheckboxControl
								key={ name }
								label={ name }
								checked={ selection.includes( name ) }
								onChange={ () =>
									setSelection(
										selection.includes( name )
											? without( selection, name )
											: [ ...selection, name ]
									)
								}
							/>
						) ) }
					</>
				) }
				{ !! onSave && (
					<p className="nelio-ab-testing-reusable-entities-dialog__manage-items">
						<Button
							variant="link"
							onClick={ () => {
								setSelection( [] );
								markAsEditing( true );
							} }
						>
							{ _x( 'Manage', 'command', 'nelio-ab-testing' ) }
						</Button>
					</p>
				) }
			</div>
			<div className="nelio-ab-testing-reusable-entities-dialog__actions">
				<Button
					variant="secondary"
					disabled={ saving }
					onClick={ onClose }
				>
					{ _x( 'Close', 'command', 'nelio-ab-testing' ) }
				</Button>

				<div className="nelio-ab-testing-reusable-entities-dialog__main-actions">
					{ !! onReplace && (
						<Button
							variant="primary"
							disabled={ ! selection.length }
							onClick={ () => {
								onReplace(
									items.filter( ( i ) =>
										selection.includes( i.attributes.name )
									)
								);
								onClose();
							} }
						>
							{ labels.replace ||
								_x( 'Replace', 'command', 'nelio-ab-testing' ) }
						</Button>
					) }

					{ !! onAdd && (
						<Button
							variant="primary"
							disabled={ ! selection.length }
							onClick={ () => {
								onAdd(
									items.filter( ( i ) =>
										selection.includes( i.attributes.name )
									)
								);
								onClose();
							} }
						>
							{ labels.add ||
								_x( 'Add', 'command', 'nelio-ab-testing' ) }
						</Button>
					) }
				</div>
			</div>
		</>
	);
}

function Editor<
	T extends { readonly attributes: { readonly name: string } },
>( {
	items,
	labels,
	saving,
	onSave,
	onCancel,
}: {
	readonly items: ReadonlyArray< T >;
	readonly labels: ManageContentProps< T >[ 'labels' ];
	readonly saving: boolean;
	readonly onSave: ( items: ReadonlyArray< T > ) => Promise< unknown >;
	readonly onCancel: () => void;
} ): JSX.Element {
	const [ workingItems, setWorkingItems ] = useState(
		items.map( ( item, index ) => ( {
			index,
			name: item.attributes.name,
		} ) )
	);
	const [ deletedItems, setDeletedItems ] = useState<
		ReadonlyArray< number >
	>( [] );

	const saveableItems = workingItems.filter(
		( wi ) => ! deletedItems.includes( wi.index )
	);
	const isDirty =
		items.length !== saveableItems.length ||
		! isEqual(
			items.map( ( i ) => i.attributes.name ),
			saveableItems.map( ( wi ) => trim( wi.name ) )
		);

	const canBeSaved =
		isDirty &&
		hasValidNames( saveableItems ) &&
		hasUniqueNames( saveableItems );

	return (
		<>
			<div className="nelio-ab-testing-reusable-entities-dialog__content">
				{ !! labels.manageElements && (
					<div>{ labels.manageElements }</div>
				) }
				{ workingItems.map( ( { index, name } ) => (
					<div
						key={ index }
						className={ clsx(
							'nelio-ab-testing-reusable-entities-dialog__working-item',
							{
								'nelio-ab-testing-reusable-entities-dialog__working-item--deleted':
									deletedItems.includes( index ),
							}
						) }
					>
						<TextControl
							className={ clsx(
								'nelio-ab-testing-reusable-entities-dialog__working-item-text',
								{
									'nelio-ab-testing-reusable-entities-dialog__working-item-text--deleted':
										deletedItems.includes( index ),
								}
							) }
							value={ name }
							disabled={ deletedItems.includes( index ) }
							onChange={ ( newName ) =>
								setWorkingItems(
									workingItems.map( ( wi ) =>
										wi.index === index
											? { index, name: newName }
											: wi
									)
								)
							}
						/>
						<Button
							icon={
								deletedItems.includes( index )
									? 'image-rotate'
									: 'trash'
							}
							onClick={ () =>
								setDeletedItems(
									deletedItems.includes( index )
										? without( deletedItems, index )
										: [ ...deletedItems, index ]
								)
							}
						/>
					</div>
				) ) }
			</div>
			<div className="nelio-ab-testing-reusable-entities-dialog__actions">
				<Button
					variant="secondary"
					disabled={ saving }
					onClick={ onCancel }
				>
					{ isDirty
						? _x( 'Discard', 'command', 'nelio-ab-testing' )
						: _x( 'Back', 'command', 'nelio-ab-testing' ) }
				</Button>
				<div className="nelio-ab-testing-reusable-entities-dialog__main-actions">
					<Button
						variant="primary"
						disabled={ ! canBeSaved }
						onClick={ () => {
							const newItems: ReadonlyArray< T > = saveableItems
								.map( ( { index, name } ) => {
									const item = items[ index ];
									if ( ! item ) {
										return undefined;
									}
									return {
										...item,
										attributes: {
											...item.attributes,
											name,
										},
									};
								} )
								.filter( isDefined );
							void onSave( newItems );
						} }
					>
						{ saving
							? _x( 'Saving…', 'text', 'nelio-ab-testing' )
							: _x( 'Save', 'command', 'nelio-ab-testing' ) }
					</Button>
				</div>
			</div>
		</>
	);
}

// =======
// HELPERS
// =======

const hasValidNames = (
	workingItems: ReadonlyArray< { readonly name: string } >
): boolean => workingItems.every( ( wi ) => !! trim( wi.name ) );

const hasUniqueNames = (
	workingItems: ReadonlyArray< { readonly name: string } >
): boolean =>
	uniq( workingItems.map( ( wi ) => trim( wi.name.toLowerCase() ) ) )
		.length === workingItems.length;
